import { useEffect, type ReactElement } from "react";
import { useSearchParams } from "react-router-dom";
import { Tabs } from "antd";
import { CheckCircleFilled } from "@ant-design/icons";
import { protocolProductTabs } from "../constants";
import {
  getDefaultActiveTabs,
  isBiologicalHasValidData,
  isSeedHasValidData,
} from "../utils";
import { useCreateProtocolForm } from "..";

const ProductsComponent = (): ReactElement => {
  const { formValidationData, formValues } = useCreateProtocolForm();
  const [searchParams, setSearchParams] = useSearchParams();
  const type = searchParams.get("type");
  const protocolTypes =
    (Array.isArray(formValues?.protocolType) && formValues?.protocolType) ||
    formValidationData?.protocolType ||
    [];

  // A country-change revalidation in flight means the current
  // products/treatments may still be the stale, pre-change selection --
  // never show a tab as complete until that revalidation has settled.
  const seedRevalidationPending = Boolean(
    formValidationData?.seedRevalidationRequired ||
      formValidationData?.seedRevalidationProcessing ||
      formValidationData?.clearProductsAndCalculations
  );
  const bioRevalidationPending = Boolean(
    formValidationData?.bioRevalidationRequired ||
      formValidationData?.clearProductsAndCalculations
  );

  const completionByType: Record<string, boolean> = {
    seed:
      !seedRevalidationPending &&
      Boolean(isSeedHasValidData(formValidationData)),
    biological:
      !bioRevalidationPending &&
      Boolean(
        isBiologicalHasValidData(
          formValues?.biologicals ? formValues?.biologicals : {}
        )
      ),
    nutrient: Boolean(formValidationData?.isNutrientDataValid),
  };

  const baseTabs = protocolProductTabs(protocolTypes);
  const baseTabKeys = baseTabs.map((tab: any) => String(tab.key));

  const tabs = baseTabs.map((tab: any, index: number) => {
    const key = String(tab.key);
    // Block jumping forward past an incomplete tab (e.g. clicking "CP and
    // Biologicals" directly while Seeds has no valid product selection).
    // Backward navigation is always allowed, matching the "Back" button.
    const hasIncompletePriorTab = baseTabKeys
      .slice(0, index)
      .some((priorKey) => !completionByType[priorKey]);

    return {
      ...tab,
      disabled: hasIncompletePriorTab,
      label: (
        <span className="product-tab-label">
          <span>{tab.label}</span>
          {completionByType[key] && (
            <CheckCircleFilled className="product-tab-check-icon" />
          )}
        </span>
      ),
    };
  });
  const tabKeys = tabs.map((tab) => String(tab.key));

  const defaultTabKey =
    type && tabKeys.includes(type) ? type : getDefaultActiveTabs(protocolTypes);

  const activeTabKey = tabKeys.includes(defaultTabKey)
    ? defaultTabKey
    : tabKeys[0];

  useEffect(() => {
    if (activeTabKey && type !== activeTabKey) {
      const updatedSearchParams = new URLSearchParams(searchParams);
      updatedSearchParams.set("type", activeTabKey);
      setSearchParams(updatedSearchParams);
    }
  }, [activeTabKey, type, searchParams, setSearchParams]);

  const onChange = (key: string): void => {
    const updatedSearchParams = new URLSearchParams(searchParams);
    updatedSearchParams.set("type", key);
    setSearchParams(updatedSearchParams);
  };

  return (
    <div className={`products-component products-component--${activeTabKey}`}>
      <Tabs activeKey={activeTabKey} items={tabs} onChange={onChange} />
    </div>
  );
};

export default ProductsComponent;
